<template>
  <AdminLayout page-title="Safety Incidents" page-subtitle="Monitor and manage safety incidents across all operations">
    <div class="space-y-8 pt-8">
      <!-- Stats Cards -->
      <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Total Incidents</p>
              <p class="text-3xl font-bold text-white">{{ stats.totalIncidents }}</p>
              <p class="text-sm text-emerald-400 mt-2">-{{ stats.reduction }}% this month</p>
            </div>
            <div class="w-12 h-12 bg-red-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-red-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Days Since Last</p>
              <p class="text-3xl font-bold text-white">{{ stats.daysSinceLast }}</p>
              <p class="text-sm text-emerald-400 mt-2">Record: {{ stats.recordDays }}</p>
            </div>
            <div class="w-12 h-12 bg-emerald-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-emerald-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Pending Reports</p>
              <p class="text-3xl font-bold text-white">{{ stats.pendingReports }}</p>
              <p class="text-sm text-yellow-400 mt-2">Awaiting review</p>
            </div>
            <div class="w-12 h-12 bg-yellow-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-yellow-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"/>
              </svg>
            </div>
          </div>
        </div>

        <div class="admin-stat-card">
          <div class="flex items-start justify-between">
            <div class="flex-1">
              <p class="text-sm font-medium text-slate-400 mb-2">Safety Score</p>
              <p class="text-3xl font-bold text-white">{{ stats.safetyScore }}/100</p>
              <p class="text-sm text-emerald-400 mt-2">+{{ stats.improvement }} this month</p>
            </div>
            <div class="w-12 h-12 bg-blue-500/20 rounded-xl flex items-center justify-center">
              <svg class="w-6 h-6 text-blue-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z"/>
              </svg>
            </div>
          </div>
        </div>
      </div>

      <!-- Actions Bar -->
      <div class="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6">
        <div class="flex items-center space-x-4">
          <div class="relative">
            <input
              v-model="searchQuery"
              type="text"
              placeholder="Search incidents..."
              class="w-64 pl-10 pr-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
            >
            <svg class="absolute left-3 top-2.5 w-5 h-5 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>
          <select
            v-model="filterSeverity"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Severity</option>
            <option value="low">Low</option>
            <option value="medium">Medium</option>
            <option value="high">High</option>
            <option value="critical">Critical</option>
          </select>
          <select
            v-model="filterStatus"
            class="px-4 py-2 bg-slate-800/50 border border-slate-600/50 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
          >
            <option value="">All Status</option>
            <option value="pending">Pending</option>
            <option value="investigating">Investigating</option>
            <option value="resolved">Resolved</option>
            <option value="closed">Closed</option>
          </select>
        </div>
        <div class="flex items-center space-x-3">
          <button @click="showCreateModal = true" class="btn-hero-primary">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 6v6m0 0v6m0-6h6m-6 0H6"/>
            </svg>
            Report Incident
          </button>
          <button @click="exportIncidents" class="btn-secondary">
            <svg class="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z"/>
            </svg>
            Export
          </button>
        </div>
      </div>

      <!-- Incidents Table -->
      <div class="bg-slate-800/40 backdrop-blur-sm rounded-2xl border border-slate-700/50 overflow-hidden">
        <div class="overflow-x-auto">
          <table class="w-full">
            <thead class="bg-slate-700/30">
              <tr>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Incident</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Location</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Severity</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Status</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Reported</th>
                <th class="px-6 py-3 text-left text-xs font-medium text-slate-400 uppercase tracking-wider">Actions</th>
              </tr>
            </thead>
            <tbody class="divide-y divide-slate-700/50">
              <tr v-for="incident in filteredIncidents" :key="incident.id" class="hover:bg-slate-700/20 transition-colors">
                <td class="px-6 py-4">
                  <div>
                    <p class="text-white font-medium">{{ incident.title }}</p>
                    <p class="text-sm text-slate-400">{{ incident.description }}</p>
                  </div>
                </td>
                <td class="px-6 py-4 text-slate-300">{{ incident.location }}</td>
                <td class="px-6 py-4">
                  <span :class="getSeverityBadgeClass(incident.severity)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ incident.severity }}
                  </span>
                </td>
                <td class="px-6 py-4">
                  <span :class="getStatusBadgeClass(incident.status)" class="px-2 py-1 rounded-full text-xs font-medium">
                    {{ incident.status }}
                  </span>
                </td>
                <td class="px-6 py-4 text-slate-300">{{ formatDate(incident.reportedAt) }}</td>
                <td class="px-6 py-4">
                  <div class="flex items-center space-x-2">
                    <button @click="viewIncident(incident)" class="text-emerald-400 hover:text-emerald-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"/>
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z"/>
                      </svg>
                    </button>
                    <button @click="editIncident(incident)" class="text-blue-400 hover:text-blue-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                      </svg>
                    </button>
                    <button @click="deleteIncident(incident.id)" class="text-red-400 hover:text-red-300">
                      <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                      </svg>
                    </button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>

    <!-- Create Incident Modal -->
    <div v-if="showCreateModal" class="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div class="bg-slate-800/90 backdrop-blur-xl rounded-2xl p-6 w-full max-w-2xl border border-slate-700/50">
        <h3 class="text-xl font-bold text-white mb-4">Report New Incident</h3>
        <form @submit.prevent="createIncident" class="space-y-4">
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Incident Title</label>
              <input
                v-model="newIncident.title"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Brief description of the incident"
              >
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Location</label>
              <input
                v-model="newIncident.location"
                type="text"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
                placeholder="Job site or location"
              >
            </div>
          </div>
          <div>
            <label class="block text-sm font-medium text-slate-300 mb-2">Description</label>
            <textarea
              v-model="newIncident.description"
              rows="3"
              required
              class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white placeholder-slate-400 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent resize-none"
              placeholder="Detailed description of what happened"
            ></textarea>
          </div>
          <div class="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Severity</label>
              <select
                v-model="newIncident.severity"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select Severity</option>
                <option value="low">Low</option>
                <option value="medium">Medium</option>
                <option value="high">High</option>
                <option value="critical">Critical</option>
              </select>
            </div>
            <div>
              <label class="block text-sm font-medium text-slate-300 mb-2">Type</label>
              <select
                v-model="newIncident.type"
                required
                class="w-full px-3 py-2 bg-slate-700/50 border border-slate-600 rounded-lg text-white focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-transparent"
              >
                <option value="">Select Type</option>
                <option value="injury">Injury</option>
                <option value="near-miss">Near Miss</option>
                <option value="equipment">Equipment Failure</option>
                <option value="environmental">Environmental</option>
                <option value="other">Other</option>
              </select>
            </div>
          </div>
          <div class="flex justify-end space-x-3 pt-4">
            <button
              type="button"
              @click="showCreateModal = false"
              class="px-4 py-2 text-slate-400 hover:text-white transition-colors"
            >
              Cancel
            </button>
            <button
              type="submit"
              class="btn-hero-primary"
            >
              Report Incident
            </button>
          </div>
        </form>
      </div>
    </div>
  </AdminLayout>
</template>

<script setup lang="ts">
import { computed, onMounted, ref } from 'vue'
import { useAdminStore } from '../../stores/adminStore'
import AdminLayout from './AdminLayout.vue'

const adminStore = useAdminStore()

// State
const stats = ref({
  totalIncidents: 0,
  reduction: 0,
  daysSinceLast: 0,
  recordDays: 0,
  pendingReports: 0,
  safetyScore: 0,
  improvement: 0
})

const incidents = ref<any[]>([])
const isLoading = ref(false)
const searchQuery = ref('')
const filterSeverity = ref('')
const filterStatus = ref('')
const showCreateModal = ref(false)

const newIncident = ref({
  title: '',
  location: '',
  description: '',
  severity: '',
  type: ''
})

// Computed
const filteredIncidents = computed(() => {
  let filtered = incidents.value

  if (searchQuery.value) {
    const q = searchQuery.value.toLowerCase()
    filtered = filtered.filter(incident =>
      String(incident.title || '').toLowerCase().includes(q) ||
      String(incident.description || '').toLowerCase().includes(q) ||
      String(incident.location || '').toLowerCase().includes(q)
    )
  }

  if (filterSeverity.value) {
    filtered = filtered.filter(incident => incident.severity === filterSeverity.value)
  }

  if (filterStatus.value) {
    filtered = filtered.filter(incident => incident.status === filterStatus.value)
  }

  return filtered.sort((a, b) => new Date(b.reportedAt).getTime() - new Date(a.reportedAt).getTime())
})

// Methods
const loadIncidents = async () => {
  try {
    isLoading.value = true
    const response = await fetch('/api/admin/safety/incidents', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const data = await response.json()
      const rows = Array.isArray(data.incidents) ? data.incidents : []
      incidents.value = rows.map((incident: any) => ({
        ...incident,
        title: incident.title || incident.type || 'Incident',
        description: incident.description || '',
        location: incident.location || '',
        severity: incident.severity || 'low',
        status: incident.status || 'open',
        reportedAt: incident.reportedAt || incident.createdAt || new Date().toISOString(),
      }))
      stats.value = {
        totalIncidents: Number(data.stats?.totalIncidents ?? incidents.value.length),
        reduction: Number(data.stats?.reduction ?? 0),
        daysSinceLast: Number(data.stats?.daysSinceLast ?? 0),
        recordDays: Number(data.stats?.recordDays ?? 0),
        pendingReports: Number(data.stats?.pendingReports ?? 0),
        safetyScore: Number(data.stats?.safetyScore ?? 0),
        improvement: Number(data.stats?.improvement ?? 0),
      }
    } else {
      // Load mock data for demo
      loadMockData()
    }
  } catch (error) {
    console.error('Error loading incidents:', error)
    loadMockData()
  } finally {
    isLoading.value = false
  }
}

const loadMockData = () => {
  incidents.value = [
    {
      id: 1,
      title: 'Minor Equipment Malfunction',
      description: 'Drill bit showed signs of wear during operation',
      location: 'Bakken Site A',
      severity: 'low',
      status: 'resolved',
      reportedAt: '2024-01-15T10:30:00Z'
    },
    {
      id: 2,
      title: 'Near Miss - Falling Object',
      description: 'Tool slipped from worker\'s hand, no injury occurred',
      location: 'Eagle Ford Site B',
      severity: 'medium',
      status: 'investigating',
      reportedAt: '2024-01-14T14:20:00Z'
    },
    {
      id: 3,
      title: 'Minor Hand Injury',
      description: 'Worker sustained small cut while handling equipment',
      location: 'Permian Basin Rig 7',
      severity: 'medium',
      status: 'closed',
      reportedAt: '2024-01-13T09:15:00Z'
    }
  ]
  
  stats.value = {
    totalIncidents: 12,
    reduction: 15,
    daysSinceLast: 45,
    recordDays: 67,
    pendingReports: 3,
    safetyScore: 92,
    improvement: 8
  }
}

const createIncident = async () => {
  try {
    const response = await fetch('/api/admin/safety/incidents', {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(newIncident.value)
    })
    
    if (response.ok) {
      const incident = await response.json()
      incidents.value.unshift(incident)
      stats.value.totalIncidents++
      showCreateModal.value = false
      newIncident.value = { title: '', location: '', description: '', severity: '', type: '' }
    } else {
      console.error('Failed to create incident')
    }
  } catch (error) {
    console.error('Error creating incident:', error)
  }
}

const viewIncident = (incident: any) => {
  // TODO: Implement view incident details
  console.log('View incident:', incident)
}

const editIncident = (incident: any) => {
  // TODO: Implement edit incident functionality
  console.log('Edit incident:', incident)
}

const deleteIncident = async (incidentId: number) => {
  if (!confirm('Are you sure you want to delete this incident?')) return
  
  try {
    const response = await fetch(`/api/admin/safety/incidents/${incidentId}`, {
      method: 'DELETE',
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      incidents.value = incidents.value.filter(incident => incident.id !== incidentId)
      stats.value.totalIncidents--
    } else {
      console.error('Failed to delete incident')
    }
  } catch (error) {
    console.error('Error deleting incident:', error)
  }
}

const exportIncidents = async () => {
  try {
    const response = await fetch('/api/admin/safety/incidents/export', {
      headers: {
        'Authorization': `Bearer ${adminStore.adminToken}`,
        'Content-Type': 'application/json'
      }
    })
    
    if (response.ok) {
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = `incidents-export-${new Date().toISOString().split('T')[0]}.csv`
      document.body.appendChild(a)
      a.click()
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)
    }
  } catch (error) {
    console.error('Error exporting incidents:', error)
  }
}

const getSeverityBadgeClass = (severity: string) => {
  const classes = {
    low: 'bg-green-500/20 text-green-400 border border-green-500/30',
    medium: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    high: 'bg-orange-500/20 text-orange-400 border border-orange-500/30',
    critical: 'bg-red-500/20 text-red-400 border border-red-500/30'
  }
  return classes[severity as keyof typeof classes] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

const getStatusBadgeClass = (status: string) => {
  const classes = {
    pending: 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30',
    investigating: 'bg-blue-500/20 text-blue-400 border border-blue-500/30',
    resolved: 'bg-emerald-500/20 text-emerald-400 border border-emerald-500/30',
    closed: 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
  }
  return classes[status as keyof typeof classes] || 'bg-slate-500/20 text-slate-400 border border-slate-500/30'
}

const formatDate = (dateString: string) => {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'short',
    day: 'numeric'
  })
}

// Lifecycle
onMounted(() => {
  loadIncidents()
})
</script>

<style scoped>
.admin-stat-card {
  @apply bg-slate-800/40 backdrop-blur-sm rounded-2xl p-6 border border-slate-700/50 hover:border-emerald-500/30 transition-all duration-300;
}

.btn-hero-primary {
  @apply inline-flex items-center px-4 py-2 bg-gradient-to-r from-emerald-500 to-cyan-500 text-white font-semibold rounded-lg hover:from-emerald-600 hover:to-cyan-600 transition-all duration-300 transform hover:scale-105 shadow-lg shadow-emerald-500/25;
}

.btn-secondary {
  @apply inline-flex items-center px-4 py-2 bg-slate-700/50 text-slate-300 font-medium rounded-lg hover:bg-slate-600/50 transition-colors duration-200;
}
</style>
